// Party-surface WS-3 — the vendor "Portal account" tab. Wraps the shared
// portal-absorption PortalAccountTab with the vendor-specific invite
// mutation (POST /vendors/{id}/portal-invite). The portal-account card
// moved OFF the Overview tab (the PartyDetailPage precedent); UAT F2's
// mode-aware invite toasts live here now.

import { useQueryClient } from "@tanstack/react-query";
import { PortalAccountTab } from "@/components/portalAbsorption/PortalAccountTab";
import type { VendorDto } from "@/api/portfolioAdmin/types";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import {
  useInviteVendorPortal,
  useVendorPortalAccount,
} from "@/hooks/portfolioAdmin/useRegistries";
import { portalAbsorptionKeys } from "@/hooks/portalAbsorption/portalAbsorptionKeys";

export function VendorPortalAccountTab({ vendor }: { vendor: VendorDto }) {
  const qc = useQueryClient();
  const auth = useAuth();
  const toast = useToast();
  const canManage =
    auth.hasRoleCode("E1-VM") ||
    auth.hasRoleCode("E5") ||
    auth.hasRoleCode("ADMIN") ||
    auth.hasPermission("Vendors.Manage");

  const portalQuery = useVendorPortalAccount(vendor.id);
  const invite = useInviteVendorPortal(vendor.id);
  const account = portalQuery.data ?? null;
  const hasEmail = !!vendor.email;

  const onInvite = async () => {
    try {
      await invite.mutateAsync();
      // UAT F2 — mode-aware confirmation mirroring what the backend actually
      // sent: fresh invite / set-password re-send / sign-in notification.
      if (!account) {
        toast.success(`Portal invitation sent to ${vendor.email}.`);
      } else if (account.isActivated) {
        toast.success(`Sign-in notification sent to ${account.email}.`);
      } else {
        toast.success(`Invitation re-sent to ${account.email} (set-password link).`);
      }
      // The shared tab's account detail + activity timeline reflect the send.
      void qc.invalidateQueries({ queryKey: portalAbsorptionKeys.all });
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  if (portalQuery.isLoading) {
    return <p className="text-caption text-gray-mid">Loading portal account…</p>;
  }

  return (
    <PortalAccountTab
      portalUserId={account?.portalUserId ?? null}
      emptyHint={
        hasEmail
          ? "No portal account is linked to this vendor yet — send the portal invitation to provision one."
          : "No portal account is linked to this vendor yet. Add an email to the vendor record first — the invitation is sent to the vendor's email on file."
      }
      invite={
        canManage && hasEmail
          ? { send: () => void onInvite(), pending: invite.isPending }
          : undefined
      }
    />
  );
}
